/**
 * PDP USP Slider Component
 * A native, lightweight, performant sliding announcement/USP ticker for product pages.
 */
class UspSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isHovered = false;
    this.observer = null;
    this.mutationObserver = null;
  }

  connectedCallback() {
    this.track = this.querySelector('.pdp-usp-slider__track');
    this.dotsContainer = this.querySelector('.pdp-usp-slider__dots');
    if (!this.track) return;

    this.init();

    // Listen for tab visibility changes
    this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Support Shopify Theme Editor live inspector events
    this.handleShopifyBlockSelect = this.onShopifyBlockSelect.bind(this);
    this.handleShopifyBlockDeselect = this.onShopifyBlockDeselect.bind(this);
    document.addEventListener('shopify:block:select', this.handleShopifyBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleShopifyBlockDeselect);

    // Observe changes to slides inside track
    this.mutationObserver = new MutationObserver(() => this.init());
    this.mutationObserver.observe(this.track, { childList: true });
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.observer) this.observer.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleShopifyBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleShopifyBlockDeselect);
  }

  get slides() {
    return Array.from(this.track ? this.track.querySelectorAll('.pdp-usp-slide') : []);
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true';
  }

  get speed() {
    return (parseFloat(this.dataset.speed) || 4) * 1000;
  }

  get pauseOnHover() {
    return this.dataset.pauseOnHover !== 'false';
  }

  init() {
    const slides = this.slides;
    if (!slides.length) return;

    this.setupDots();
    this.setupObserver();
    this.setupHoverListeners();

    if (this.autoplayEnabled && slides.length > 1) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
  }

  setupDots() {
    if (!this.dotsContainer) return;
    const slides = this.slides;

    if (slides.length <= 1) {
      this.dotsContainer.style.display = 'none';
      return;
    }

    this.dotsContainer.style.display = 'flex';
    this.dotsContainer.innerHTML = '';

    slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pdp-usp-slider__dot' + (i === this.currentIndex ? ' is-active' : '');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Slide ${i + 1} of ${slides.length}`);
      dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');

      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(i);
      });

      this.dotsContainer.appendChild(dot);
    });
  }

  setupObserver() {
    if (this.observer) this.observer.disconnect();
    const slides = this.slides;
    if (slides.length <= 1) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const index = slides.indexOf(entry.target);
          if (index !== -1 && index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateActiveDot(index);
          }
        }
      });
    }, {
      root: this.track,
      threshold: 0.5,
    });

    slides.forEach((slide) => this.observer.observe(slide));
  }

  setupHoverListeners() {
    if (!this.pauseOnHover) return;

    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
    });

    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
    });

    this.addEventListener('touchstart', () => {
      this.isHovered = true;
    }, { passive: true });

    this.addEventListener('touchend', () => {
      setTimeout(() => {
        this.isHovered = false;
      }, 1500);
    }, { passive: true });
  }

  updateActiveDot(index) {
    if (!this.dotsContainer) return;
    const dots = this.dotsContainer.querySelectorAll('.pdp-usp-slider__dot');
    dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  goToSlide(index) {
    const slides = this.slides;
    if (!slides.length) return;

    this.currentIndex = (index + slides.length) % slides.length;
    const targetSlide = slides[this.currentIndex];

    if (targetSlide && this.track) {
      const scrollLeft = targetSlide.offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }

    this.updateActiveDot(this.currentIndex);
  }

  next() {
    this.goToSlide(this.currentIndex + 1);
  }

  previous() {
    this.goToSlide(this.currentIndex - 1);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.slides.length <= 1) return;

    this.autoplayTimer = setInterval(() => {
      if (this.isHovered || document.hidden) return;
      this.next();
    }, this.speed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay();
    } else if (this.autoplayEnabled && this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  onShopifyBlockSelect(e) {
    if (e.target && this.contains(e.target)) {
      const slide = e.target.classList.contains('pdp-usp-slide') ? e.target : e.target.closest('.pdp-usp-slide');
      if (slide) {
        const index = this.slides.indexOf(slide);
        if (index !== -1) {
          this.goToSlide(index);
          this.stopAutoplay();
        }
      }
    }
  }

  onShopifyBlockDeselect(e) {
    if (e.target && this.contains(e.target)) {
      if (this.autoplayEnabled && this.slides.length > 1) {
        this.startAutoplay();
      }
    }
  }
}

if (!customElements.get('usp-slider')) {
  customElements.define('usp-slider', UspSlider);
}
